import { addMinutes, differenceInMinutes, subMinutes } from 'date-fns';
import { zonedTimeToUtc } from 'date-fns-tz';
import { useLocationHistory } from 'features/locations/api/getLocationHistory';
import { useEffect, useMemo, useState } from 'react';
import { Weather } from 'types/models/Weather';
import { useUserDateTime } from '../../timezone/hooks/useUserDateTime';

export const useGetHistoricalWeatherConditions = (
  locationIdForAnalyzer: number | null | undefined,
  currentTimestamp: string | undefined,
  disabled: boolean
) => {
  const currentTimestampAsDate = useMemo(
    () => (currentTimestamp ? new Date(currentTimestamp) : new Date()),
    [currentTimestamp]
  );
  const { timezone } = useUserDateTime();
  const { refetch, data: location } = useLocationHistory({
    locationId: locationIdForAnalyzer,
    begin: zonedTimeToUtc(
      subMinutes(currentTimestampAsDate, 8),
      timezone
    ).toISOString(),
    end: zonedTimeToUtc(
      addMinutes(currentTimestampAsDate, 8),
      timezone
    ).toISOString(),
    config: {
      enabled: false,
      keepPreviousData: true,
    },
  });
  const [historicalWeatherConditions, setHistoricalWeatherConditions] =
    useState<Weather | undefined>(undefined);
  useEffect(() => {
    const fetchConditions = async () => {
      if (disabled || !currentTimestamp || !locationIdForAnalyzer) return;
      if (
        historicalWeatherConditions?.time &&
        Math.abs(
          differenceInMinutes(
            currentTimestampAsDate,
            new Date(historicalWeatherConditions.time)
          )
        ) < 7
      )
        return;
      await refetch();
      if (!location || !location.weather) {
        setHistoricalWeatherConditions(undefined);
        return;
      }
      setHistoricalWeatherConditions(location.weather[0]);
    };
    fetchConditions();
  }, [
    location,
    refetch,
    currentTimestampAsDate,
    disabled,
    currentTimestamp,
    historicalWeatherConditions?.time,
    locationIdForAnalyzer,
  ]);
  return historicalWeatherConditions;
};
